import React from "react";
import { ConfidencePill } from "../Pills";
import { fmt, shortDate } from "../../lib/format";
import { Cloud, CloudRain, Thermometer, Wind, Eye, RefreshCw } from "lucide-react";

function Mini({ label, value, unit, Icon }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-muted-foreground">
        {Icon && <Icon className="w-3 h-3" />}
        <span className="label-tiny" style={{ letterSpacing: "0.15em" }}>{label}</span>
      </div>
      <div className="font-heading text-xl mt-1.5 font-medium">
        {value}{unit && <span className="text-xs text-muted-foreground ml-1">{unit}</span>}
      </div>
    </div>
  );
}

/**
 * Compact 6-cell mini-strip showing the most-recent weather row, plus a
 * "Sync Open-Meteo" button that the parent wires up. Renders an empty-state
 * card when no weather rows exist yet.
 */
export default function WeatherMini({ latest, confidence, syncing, onSync }) {
  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="label-tiny flex items-center gap-2"><Cloud className="w-3.5 h-3.5" /> Weather</h2>
          <ConfidencePill level={confidence} />
        </div>
        <button
          onClick={onSync}
          disabled={syncing}
          className="text-xs inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-border hover:bg-secondary disabled:opacity-50"
          data-testid="weather-sync-button"
        >
          <RefreshCw className={`w-3 h-3 ${syncing ? "animate-spin" : ""}`} />
          {syncing ? "Syncing…" : "Sync Open-Meteo"}
        </button>
      </div>
      {latest ? (
        <div className="card-base p-5 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4" data-testid="weather-mini">
          <Mini label="Date" value={shortDate(latest.date)} Icon={Cloud} />
          <Mini label="Temp avg" value={fmt(latest.temp_avg_c, 1)} unit="°C" Icon={Thermometer} />
          <Mini label="Rain" value={fmt(latest.rain_mm, 1)} unit="mm" Icon={CloudRain} />
          <Mini label="Wind" value={fmt(latest.wind_speed_ms, 1)} unit="m/s" Icon={Wind} />
          <Mini label="Gust" value={fmt(latest.wind_gust_ms, 1)} unit="m/s" Icon={Wind} />
          <Mini label="UV" value={fmt(latest.uv_index, 1)} Icon={Eye} />
        </div>
      ) : (
        <div className="card-base p-5 text-sm text-muted-foreground" data-testid="weather-empty">
          No weather data yet. Tap <span className="text-foreground">Sync Open-Meteo</span> to pull the last 30 days.
        </div>
      )}
    </section>
  );
}
